// Platform-admin capability probe (fn-71 / ADR 0035 / R11).
//
// The console has NO client-side admin signal: `whoAmI` carries identity only,
// and the platform-admin authority is a control-plane email allowlist
// (`isPlatformAdmin`) the SPA can't replicate. So to hide the Add/Edit-Agent
// affordances from non-admins we PROBE the agent-write-path's own admin-gated
// endpoint — `agents.draftConfigurableTools` with no servers — and treat
// success as "admin", a (mapped) error as "not admin".
//
// **Cost** — session-cached (`staleTime`/`gcTime` Infinity, no retry) so it
// costs ONE request per browser session, shared across the agents list +
// detail + form via the tanstack-query cache (the `{ mcp_servers: [] }` key is
// also the form's initial draft-tools key, so it dedupes). The CP rejects a
// non-admin BEFORE any catalog read, so the not-admin path is cheap.
//
// **Fail-closed** — only an explicit success reveals the admin affordance; a
// loading/errored/unknown probe hides it. Better to hide a button than to show
// one that 403s on click.
//
// **Only an authorization failure is a verdict** — a `403`/`FORBIDDEN` is the CP
// definitively saying "not admin" (resolved → not-admin). A transient error
// (5xx / network / catalog read failure) is NOT a denial: it stays retryable and
// UNRESOLVED (the page keeps "Checking access…" rather than caching a session-
// long false negative). Caching a transient failure as denial was the bug.

import { trpc } from '../../trpc-client.js';

export interface PlatformAdminState {
  /** `true` ONLY when the admin probe has resolved successfully. */
  readonly isAdmin: boolean;
  /** `true` once the probe has settled to a verdict (success OR a 403). */
  readonly isResolved: boolean;
}

/** Is this tRPC error an explicit authorization failure (403 / FORBIDDEN)? */
function isForbiddenError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const data = (error as { data?: { httpStatus?: number; code?: string } }).data;
  if (data === undefined || data === null) return false;
  return data.httpStatus === 403 || data.code === 'FORBIDDEN';
}

/** Probe whether the current user may author agents (platform admin). */
export function useIsPlatformAdmin(): PlatformAdminState {
  const probe = trpc.agents.draftConfigurableTools.useQuery(
    { mcp_servers: [] },
    {
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
      // A 403 is a definite verdict — don't retry it. Any other error is
      // transient — retry it (bounded) so it isn't cached as a denial.
      retry: (failureCount, error) => !isForbiddenError(error) && failureCount < 3,
    },
  );
  const forbidden = probe.isError && isForbiddenError(probe.error);
  return {
    isAdmin: probe.isSuccess,
    // Resolved ONLY on success or an explicit 403 — a transient error leaves the
    // probe unresolved (retryable), never a cached not-admin.
    isResolved: probe.isSuccess || forbidden,
  };
}
